import { useEffect, useState } from "react";
import { X, Copy, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";

// VAGUE 43 — Argumentaire en 6 parties (IA personnalisée ou version standard).
const copy = async (t) => {
  try { await navigator.clipboard.writeText(t); toast.success("Copié"); } catch { /* noop */ }
};

function Line({ text, testid }) {
  return (
    <div className="flex items-start gap-2 text-sm leading-snug" data-testid={testid}>
      <span className="flex-1">{text}</span>
      <button type="button" onClick={() => copy(text)} className="p-1 text-muted-foreground hover:text-primary shrink-0" aria-label="Copier">
        <Copy size={12}/>
      </button>
    </div>
  );
}

function Section({ n, title, children, testid }) {
  return (
    <section className="border border-border rounded-sm p-3 bg-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1.5">{n}. {title}</div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

export default function SalesScriptModal({ biz, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (force = false) => {
    setLoading(true);
    try {
      const r = await teleproApi.get(`/ai/argumentaire/${biz.id}${force ? "?force=true" : ""}`);
      setData(r.data);
    } catch {
      toast.error("Argumentaire indisponible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [biz.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[85] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid={`sales-script-modal-${biz.id}`}>
      <div className="bg-background border border-border rounded-sm shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-primary/5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Argumentaire</div>
            <div className="font-display font-semibold text-sm truncate">{biz.name}</div>
          </div>
          <div className="flex items-center gap-1.5">
            {data && (
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${data.source === "ai" ? "bg-primary/10 text-primary border-primary/30" : "bg-muted text-muted-foreground border-border"}`}
                data-testid="sales-script-source">
                {data.source === "ai" ? "Personnalisé IA" : "Version standard"}
              </span>
            )}
            <button type="button" onClick={() => load(true)} disabled={loading} className="p-1.5 text-muted-foreground hover:text-primary disabled:opacity-50" aria-label="Régénérer" data-testid="sales-script-refresh">
              <ArrowsClockwise size={15} className={loading ? "animate-spin" : ""}/>
            </button>
            <button type="button" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground" aria-label="Fermer" data-testid="sales-script-close">
              <X size={16}/>
            </button>
          </div>
        </div>
        <div className="overflow-y-auto p-3 sm:p-4 space-y-2.5">
          {loading && !data ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2 py-8 justify-center" data-testid="sales-script-loading">
              <Sparkle size={14} className="animate-pulse"/> Préparation de l&apos;argumentaire…
            </div>
          ) : data && (
            <>
              {data.source !== "ai" && (
                <p className="text-[11px] text-muted-foreground bg-muted/50 border border-border rounded-sm px-2 py-1.5" data-testid="sales-script-standard-note">
                  L&apos;IA n&apos;est pas disponible pour le moment : voici l&apos;argumentaire standard adapté à cette fiche.
                </p>
              )}
              {data.sector_tip && <p className="text-[11px] bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">💡 {data.sector_tip}</p>}
              <Section n={1} title="Accroche" testid="sales-script-hook"><Line text={data.hook}/></Section>
              <Section n={2} title="Questions de découverte" testid="sales-script-questions">
                {data.discovery_questions.map((q, i) => <Line key={i} text={q} testid={`sales-script-question-${i}`}/>)}
              </Section>
              <Section n={3} title="Bénéfices MonCommerce68" testid="sales-script-benefits">
                {data.benefits.map((b, i) => <Line key={i} text={`• ${b}`}/>)}
              </Section>
              <Section n={4} title="Réponses aux objections" testid="sales-script-objections">
                {data.objections.map((o, i) => (
                  <div key={i}>
                    <div className="text-[11px] font-bold text-secondary">{o.objection}</div>
                    <Line text={o.response.replace(/\*\*/g, "")}/>
                  </div>
                ))}
              </Section>
              <Section n={5} title="Proposition commerciale" testid="sales-script-offer"><Line text={data.offer}/></Section>
              <Section n={6} title="Conclusion" testid="sales-script-closing"><Line text={data.closing}/></Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
